'use client';
import React, { useState } from 'react';
import { Box } from '@osmosis/react-ui/components/Box';
import { Flex } from '@osmosis/react-ui/components/Box';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { IconButton } from '@osmosis/react-ui/components/IconButton';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import themeSchema, { getTextColor } from '../../../lib/composer/schemas/theme';
import { ctaSchema, ctasSchema } from '../../../lib/composer/schemas/cta';
import textSchema from '../../../lib/composer/schemas/text';
import subheadingSchema from '../../../lib/composer/schemas/subheading';
import headingSchema from '../../../lib/composer/schemas/heading';
import mediaSchema from '../../../lib/composer/schemas/media';
import CTAs from '../block-ui/CTAs';
import BlockWrapper from '../BlockWrapper';
import Heading from '../block-ui/Heading';
import Subheading from '../block-ui/Subheading';
import Media from '../block-ui/Media';
import RichText from '../block-ui/RichText';
import EditablePropsType, {
  ComposerBlockElement,
  MediaCarouselBlockProps,
} from '../../../types/composer';
import { transparentize } from '@osmosis/shared/styles/colorModifiers';
import useStack, { ANIMATIONS } from '../../../hooks/useStack';
import StackTransition from '../../StackTransition';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';

import Pagination from '../../learn/blocks/notes-block/Pagination';
import { clickable } from '../../../styles/hover';

/**
 * Display a media carousel component.
 */
const MediaCarousel: ComposerBlockElement = (
  props: MediaCarouselBlockProps,
) => {
  const {
    heading,
    subheading,
    text,
    cta: ctaProps,
    ctas: ctasProps,
    theme = 'primary',
    media,
    topHeader,
  } = props;

  const colors = useColors();

  const bg = (theme && colors[theme]) || 'bg-0';

  const ctas = [...(ctaProps?.caption ? [ctaProps] : []), ...(ctasProps || [])];

  const [index, setIndex] = useState(0);

  const carouselItems =
    media?.map((m, i) => {
      const mediaCtas = [...(m.cta?.caption ? [m.cta] : []), ...(m.ctas || [])];

      return (
        <Box
          key={i}
          className="flex gap-8 sm:gap-5 sm:flex-row flex-col items-center h-[500px]"
        >
          {m.src && (
            <Box width={480}>
              <Media media={m} />
            </Box>
          )}

          <Box
            bg={transparentize(0.9, '#fff')}
            p={3}
            sx={{ borderRadius: 10 }}
            className="flex flex-col gap-8"
          >
            <Box>
              {m.heading && (
                <Heading
                  text={m.heading}
                  theme={theme}
                  topHeader={topHeader}
                  textProps="text-center"
                />
              )}

              {m.subheading && (
                <Subheading
                  text={m.subheading}
                  theme={theme}
                  textProps="text-center"
                  topHeader={topHeader}
                />
              )}

              {m.text && <RichText theme={theme} html={m.text} />}
            </Box>
            <CTAs ctas={mediaCtas} theme={theme} containerCN="justify-center" />
          </Box>
        </Box>
      );
    }) || [];

  const { navigate, animation, current } = useStack(carouselItems[index]);

  const handleNavigate = (delta: number): void => {
    let newIndex = index + delta;

    // Go to end if less than 0
    if (newIndex < 0) newIndex = carouselItems.length - 1;
    // Go to start if greater than length
    else if (newIndex > carouselItems.length - 1) newIndex = 0;

    // Save index and navigate
    setIndex(newIndex);
    navigate(carouselItems[newIndex], {
      animation: delta > 0 ? ANIMATIONS.forward : ANIMATIONS.backward,
    });
  };

  const textColor = getTextColor(theme);

  return (
    <Box
      sx={{
        backgroundImage:
          theme === 'legacy'
            ? 'url(https://d3cdo0emj8d2qc.cloudfront.net/assets/0Z4X5EaGT_yLB0kLrSt2LjxVRRSLlP1-.jpg)'
            : 'none',
        backgroundColor: theme === 'legacy' ? 'transparent' : bg,
      }}
    >
      <BlockWrapper>
        <Flex flexDirection={['column']} alignItems="center" margin="auto">
          <Flex mb={4} flexDirection="column" sx={{ gap: 3 }} flex={1} mx={3}>
            {heading && (
              <Heading
                text={heading}
                theme={theme}
                textProps="text-center"
                topHeader={topHeader}
              />
            )}

            {subheading && (
              <Subheading
                text={subheading}
                theme={theme}
                textProps="text-center"
              />
            )}

            {text && <RichText theme={theme} html={text} />}
            <CTAs ctas={ctas} theme={theme} />
          </Flex>

          <Flex
            alignItems="center"
            justifyContent="space-between"
            sx={{ gap: 4 }}
            width="100%"
            mb={3}
          >
            <Box>
              <IconButton
                variant="transparent"
                round
                color={textColor}
                icon={<Icon icon={faChevronLeft} />}
                onClick={() => handleNavigate(-1)}
                data-testid="chevron-back-btn"
              />
            </Box>
            <StackTransition animation={animation} current={current} />
            <Box>
              <IconButton
                variant="transparent"
                round
                color={textColor}
                icon={<Icon icon={faChevronRight} />}
                onClick={() => handleNavigate(1)}
                data-testid="chevron-next-btn"
              />
            </Box>
          </Flex>

          <Pagination
            currentIndex={index}
            pages={carouselItems?.length || 0}
            onSelect={(i) => handleNavigate(i - index)}
            buttonProps={{
              style: {
                ...clickable(
                  transparentize(0.9, colors[textColor]),
                  transparentize(0.8, colors[textColor]),
                ),
              },
            }}
          />
        </Flex>
      </BlockWrapper>
    </Box>
  );
};

MediaCarousel.schema = {
  name: 'media-carousel',
  label: 'Media Carousel',
  editableProps: [
    headingSchema,
    subheadingSchema,
    textSchema,
    {
      label: 'Media',
      name: 'media',
      type: EditablePropsType.Array,
      items: {
        type: EditablePropsType.Object,
        name: 'item',
        label: 'Item',
        properties: {
          heading: headingSchema,
          subheading: subheadingSchema,
          text: textSchema,
          ...mediaSchema.properties,
          ctas: ctasSchema,
        },
      },
    },
    ctaSchema,
    ctasSchema,
    themeSchema,
  ],
};

export default MediaCarousel;
